import { ApiError } from "@/lib/api/errors";
import {
  mockAlerts,
  mockAuditTrail,
  mockRoles,
  mockScreenshots,
  mockTrailDetail,
  mockUsers,
  mockVideos,
} from "@/mock-data";
import type { AppRole } from "@/lib/auth/rbac-config";
import { PERMISSION_SEEDS, permissionCode } from "@/seeders/permissions.seed";
import { ROLE_PERMISSION_CODES } from "@/seeders/role-permissions.seed";
import type {
  AlertAction,
  ApiAdapter,
  AuditDetailItem,
  DashboardPeriod,
  DashboardStats,
  HistoryRow,
  ListAlertsParams,
  ListScreenshotsParams,
  ListUsersParams,
  PaginatedResult,
  Permission,
  Role,
  Screenshot,
} from "@/types/api-types";
import type { Alert, Video } from "@/types/alerts-type";
import type { User } from "@/types/users-type";
import { login as authLogin, logout as authLogout } from "@/lib/auth";

const MOCK_DELAY_MS = 400;

function delay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
}

function normalizeUserStatus(
  status: string,
): User["status"] {
  const value = status.toLowerCase();
  if (value === "active") return "active";
  if (value === "invited" || value === "pending") return "pending";
  return "inactive";
}

const mockPermissionCatalog: Permission[] = PERMISSION_SEEDS.map((seed) => ({
  id: permissionCode(seed),
  name: seed.name,
  resource: seed.resource,
  action: seed.action,
}));

function mapMockRole(role: Role): Role {
  const code = (role.code ?? role.name ?? "").trim().toUpperCase() as AppRole;
  const assignedCodes = new Set(ROLE_PERMISSION_CODES[code] ?? []);
  const permissionDetails = mockPermissionCatalog.filter((permission) =>
    assignedCodes.has(
      permissionCode({
        name: permission.name,
        resource: permission.resource,
        action: permission.action,
      }),
    ),
  );

  return {
    ...role,
    permissions: permissionDetails.length,
    permissionDetails,
  };
}

const dashboardStats: Record<DashboardPeriod, DashboardStats> = {
  "This Month": {
    videos: 142,
    alerts: 38,
    approvedAlerts: 94,
    pendingReviews: 12,
    vChange: "+12% from last month",
    aChange: "+5% from last month",
  },
  "Last Month": {
    videos: 0,
    alerts: 0,
    approvedAlerts: 0,
    pendingReviews: 0,
    vChange: "0% from prior",
    aChange: "0% from prior",
  },
  "Last 6 Months": {
    videos: 0,
    alerts: 0,
    approvedAlerts: 0,
    pendingReviews: 0,
    vChange: "0% YoY",
    aChange: "0% YoY",
  },
  "Custom": {
    videos: 0,
    alerts: 0,
    approvedAlerts: 0,
    pendingReviews: 0,
    vChange: "0% overall",
    aChange: "0% overall",
  },
};

// const dashboardActivity: Record<DashboardPeriod, DashboardActivity[]> = {
//   "This Month": [
//     {
//       action: "Video uploaded",
//       detail: "cleanroom_batch_042.mp4",
//       time: "2 min ago",
//     },
//     {
//       action: "Alert generated",
//       detail: "Contamination detected in Zone B",
//       time: "15 min ago",
//     },
//     {
//       action: "Evaluation complete",
//       detail: "cleanroom_batch_041.mp4",
//       time: "1 hr ago",
//     },
//   ],
//   "Last Month": [],
//   "Last 6 Months": [],
//   "Last Year": [],
// };

export const mockAdapter: ApiAdapter = {
  auth: {
    async login() {
      await delay();
      await authLogin();
    },
    async logout() {
      await delay();
      await authLogout();
    },
  },
  users: {
    async list(params: ListUsersParams): Promise<PaginatedResult<User>> {
      await delay();
      const search = params.search?.toLowerCase() ?? "";
      const page = params.page ?? 1;
      const pageSize = params.pageSize ?? 10;

      const filtered = mockUsers.filter(
        (u) =>
          !search ||
          u.name.toLowerCase().includes(search) ||
          u.email.toLowerCase().includes(search) ||
          u.designation.toLowerCase().includes(search),
      );

      const data = filtered.map((u) => ({
        id: String(u.id),
        name: u.name,
        email: u.email,
        designation: u.designation,
        lastLogin: u.lastLogin,
        role: u.role,
        invitedBy: u.invitedBy,
        status: normalizeUserStatus(u.status),
      }));

      const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
      const start = (page - 1) * pageSize;

      return {
        data: data.slice(start, start + pageSize),
        page,
        totalPages,
        total: data.length,
      };
    },
  },
  roles: {
    async list(): Promise<Role[]> {
      await delay();
      return (mockRoles as Role[]).map((role) => mapMockRole(role));
    },
    async getById(id: string): Promise<Role> {
      await delay();
      const role = (mockRoles as Role[]).find((item) => String(item.id) === id);
      if (!role) {
        throw new ApiError("We couldn't find this role.", 404);
      }
      return mapMockRole(role);
    },
    async updatePermissions(id: string, permissionIds: string[]): Promise<void> {
      void id;
      void permissionIds;
      await delay();
    },
  },
  permissions: {
    async list(): Promise<Permission[]> {
      await delay();
      return mockPermissionCatalog;
    },
  },
  alerts: {
    async list(params?: ListAlertsParams): Promise<Alert[]> {
      await delay();
      return mockAlerts
        .filter((alert) => {
          const videoMatch = params?.videoId
            ? alert.videoId === params.videoId
            : true;
          const statusMatch =
            !params?.status ||
            params.status === "all" ||
            alert.status.toLowerCase() === params.status.toLowerCase();
          return videoMatch && statusMatch;
        })
        .map((alert) => {
          const video = mockVideos.find((v) => v.id === alert.videoId);
          return {
            ...alert,
            id: String(alert.id),
            video: {
              filename: video?.name ?? "Unknown",
            },
          };
        });
    },
    async updateStatus(id: number, action: AlertAction): Promise<Alert> {
      await delay();
      const alert = mockAlerts.find((a) => a.id === id);
      if (!alert) throw new Error(`Alert ${id} not found`);
      const status = action === "approve" ? "Approved" : "Rejected";
      const video = mockVideos.find((v) => v.id === alert.videoId);
      return {
        ...alert,
        id: String(alert.id),
        status,
        video: {
          filename: video?.name ?? "Unknown",
        },
      };
    },
  },
  videos: {
    async list(): Promise<Video[]> {
      await delay();
      return mockVideos.map((v) => ({
        id: v.id,
        name: v.name,
        alerts: v.alerts,
      }));
    },
    async getById(id: number): Promise<Video | null> {
      await delay();
      const video = mockVideos.find((v) => v.id === id);
      if (!video) return null;
      return { id: video.id, name: video.name, alerts: video.alerts };
    },
  },
  screenshots: {
    async list(params?: ListScreenshotsParams): Promise<Screenshot[]> {
      await delay();
      return mockScreenshots.filter((s) =>
        params?.videoId ? s.videoId === params.videoId : true,
      ) as Screenshot[];
    },
  },
  auditTrail: {
    async list() {
      await delay();
      return mockAuditTrail;
    },
    async getDetail(videoName: string): Promise<AuditDetailItem[]> {
      await delay();
      const video = mockVideos.find((v) => v.name === videoName);
      if (!video) return [];
      return mockTrailDetail.filter(
        (item) => item.videoId === video.id,
      ) as AuditDetailItem[];
    },
  },
  history: {
    async list(): Promise<HistoryRow[]> {
      await delay();
      return mockVideos.map((video) => {
        const uploadAudit = mockAuditTrail.find(
          (a) => a.video === video.name && a.action === "Upload",
        );
        return {
          id: video.id,
          video: video.name,
          uploadedAt: uploadAudit?.time ?? "—",
          uploadedBy: uploadAudit?.user ?? "System",
          alerts: mockAlerts.filter((a) => a.videoId === video.id).length,
          screenshots: mockScreenshots.filter((s) => s.videoId === video.id)
            .length,
          auditTrail: video.audit,
          deviation: video.deviation,
        };
      });
    },
  },
  dashboard: {
    async getStats(period: DashboardPeriod): Promise<DashboardStats> {
      await delay();
      return dashboardStats[period];
    },
    // async getActivity(period: DashboardPeriod): Promise<DashboardActivity[]> {
    //   await delay();
    //   return dashboardActivity[period];
    // },
  },
  uploads: {
    async uploadVideos(files: File[]): Promise<{ jobId: string }> {
      await delay();
      return { jobId: `mock-job-${files.length}-${Date.now()}` };
    },
  },
};
